
import { Institution, Participant, Trial } from "@/api/dtos";
import { createContext } from "react";

export interface ILinkContext {

    trialLinkEnabled: boolean;
    createTrialLink: (trial: Trial) => string;
    setCreateTrialLink: (createLink?: (trial: Trial) => string) => void;
    setTrialLinkEnabled: (enabled?: boolean) => void;

    institutionLinkEnabled: boolean;
    createInstitutionLink: (trial: Trial, institution: Institution) => string;
    setCreateInstitutionLink: (createLink?: (trial: Trial, institution: Institution) => string) => void;
    setInstitutionLinkEnabled: (enabled?: boolean) => void;

    participantLinkEnabled: boolean;
    createParticipantLink: (trial: Trial, institution: Institution | undefined, participant: Participant) => string;
    setCreateParticipantLink: (createLink?: (trial: Trial, institution: Institution | undefined, participant: Participant) => string) => void;
    setParticipantLinkEnabled: (enabled?: boolean) => void;
}

export const DefaultTrialCreateLink = (trial: Trial) => {
    return `/${trial.oid}`
};

export const DefaultInstitutionCreateLink = (trial: Trial, institution: Institution) => {
    if (institution == null) {
        return `/${trial.oid}`;
    }

    return `/${trial.oid}/institutions/${institution.code}`
};

export const DefaultParticipantCreateLink = (trial: Trial, institution: Institution | undefined, participant: Participant) => {
    if (participant == null) {
        if (institution == null) {
            return `/${trial.oid}`;
        }

        return `/${trial.oid}/institutions/${institution.code}`
    }

    if (institution == null) {
        return `/${trial.oid}/participants/${participant.studyNumber}`
    }

    return `/${trial.oid}/institutions/${institution.code}/participants/${participant.studyNumber}`
};

export const ParticipantTrialCreateLink = (trial: Trial) => {
    return `/${trial.oid}/participants`
};

export const ParticipantInstitutionCreateLink = (trial: Trial, institution: Institution) => {
    if (institution == null) {
        return `/${trial.oid}/participants`;
    }

    return `/${trial.oid}/institutions/${institution.code}/participants`
};

export const BulkAddNotesLink = (trial: Trial) => {
    return `/${trial.oid}/notes-and-queries/bulk-add`
};

export const NotesAndQueriesTrialCreateLink = (trial: Trial) => {
    return `/${trial.oid}/notes-and-queries`
};

export const NotesAndQueriesInstitutionCreateLink = (trial: Trial, institution: Institution) => {
    if (institution == null) {
        return `/${trial.oid}/notes-and-queries`;
    }

    return `/${trial.oid}/institutions/${institution.code}/notes-and-queries`
};

export const NotesAndQueriesParticipantCreateLink = (trial: Trial, institution: Institution | undefined, participant: Participant) => {
    if (participant == null) {
        if (institution == null) {
            return `/${trial.oid}/notes-and-queries`;
        }

        return `/${trial.oid}/institutions/${institution.code}/notes-and-queries`
    }

    if (institution == null) {
        return `/${trial.oid}/participants/${participant.studyNumber}/notes-and-queries`
    }

    return `/${trial.oid}/institutions/${institution.code}/participants/${participant.studyNumber}/notes-and-queries`
};

export const ReportTrialCreateLink = (trial: Trial) => {
    return `/${trial.oid}/report${location.search}`
};

export const ReportInstitutionCreateLink = (trial: Trial, institution: Institution) => {
    if (institution == null) {
        return `/${trial.oid}/report${location.search}`;
    }

    return `/${trial.oid}/institutions/${institution.code}/report${location.search}`
};

export const ReportParticipantCreateLink = (trial: Trial, institution: Institution | undefined, participant: Participant) => {
    if (participant == null) {
        if (institution == null) {
            return `/${trial.oid}/report${location.search}`;
        }

        return `/${trial.oid}/institutions/${institution.code}/report${location.search}`
    }

    if (institution == null) {
        return `/${trial.oid}/participants/${participant.studyNumber}/report${location.search}`
    }

    return `/${trial.oid}/institutions/${institution.code}/participants/${participant.studyNumber}/report${location.search}`
};

export const SourceDataFindingsTrialCreateLink = (trial: Trial) => {
    return `/${trial.oid}/findings`
};

export const SourceDataFindingsInstitutionCreateLink = (trial: Trial, institution: Institution) => {
    if (institution == null) {
        return `/${trial.oid}/findings`;
    }

    return `/${trial.oid}/institutions/${institution.code}/findings`
};

export const SourceDataFindingsParticipantCreateLink = (trial: Trial, institution: Institution | undefined, participant: Participant) => {
    if (participant == null) {
        if (institution == null) {
            return `/${trial.oid}/findings`;
        }

        return `/${trial.oid}/institutions/${institution.code}/findings`
    }

    if (institution == null) {
        return `/${trial.oid}/participants/${participant.studyNumber}/findings`
    }

    return `/${trial.oid}/institutions/${institution.code}/participants/${participant.studyNumber}/findings`
};


export const ConfigurationTrialCreateLink = (trial: Trial) => {
    return `/${trial.oid}/configuration`
};

export const ConfigurationInstitutionCreateLink = (trial: Trial, institution: Institution) => {
    return `/${trial.oid}/configuration`;
};

export const ConfigurationTrialTrialCreateLink = (trial: Trial) => {
    return `/${trial.oid}/configuration/trial`
};

export const ConfigurationTrialInstitutionCreateLink = (trial: Trial, institution: Institution) => {
    return `/${trial.oid}/configuration/trial`;
};

export const ConfigurationInstitutionsTrialCreateLink = (trial: Trial) => {
    return `/${trial.oid}/configuration/institutions`
};

export const ConfigurationInstitutionsInstitutionCreateLink = (trial: Trial, institution: Institution) => {
    return `/${trial.oid}/configuration/institutions/${institution.code}`
};

export const ConfigurationMasterGroupsTrialCreateLink = (trial: Trial) => {
    return `/${trial.oid}/configuration/master-groups`
};

export const ConfigurationCoordinatingGroupsTrialCreateLink = (trial: Trial) => {
    return `/${trial.oid}/configuration/coordinating-groups`
};

export const ConfigurationParticipantsTrialCreateLink = (trial: Trial) => {
    return `/${trial.oid}/configuration/participants`
};

export const ConfigurationParticipantsInstitutionCreateLink = (trial: Trial, institution: Institution) => {
    return `/${trial.oid}/configuration/institutions/${institution.code}/participants`;
};

export const ConfigurationParticipantsParticipantCreateLink = (trial: Trial, institution: Institution | undefined, participant: Participant) => {
    if (institution == null) {
        return `/${trial.oid}/configuration/participants/${participant.studyNumber}`;
    }

    return `/${trial.oid}/configuration/institutions/${institution.code}/participants/${participant.studyNumber}`;
};


export const ConfigurationSourceDatasTrialCreateLink = (trial: Trial) => {
    return `/${trial.oid}/configuration/source-data`
};

export const LinkContext = createContext<ILinkContext>({
    trialLinkEnabled: true,
    createTrialLink: DefaultTrialCreateLink,
    setCreateTrialLink: () => {

    },
    setTrialLinkEnabled: () => {

    },

    institutionLinkEnabled: true,
    createInstitutionLink: DefaultInstitutionCreateLink,
    setCreateInstitutionLink: () => {

    },
    setInstitutionLinkEnabled: () => {

    },

    participantLinkEnabled: true,
    createParticipantLink: DefaultParticipantCreateLink,
    setCreateParticipantLink: () => {

    },
    setParticipantLinkEnabled: () => {

    }
})